import React, { forwardRef, useMemo } from 'react';
import { Button as MuiButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDsTokens } from '../theme/useDsTokens';
import { DsResolvedTokens } from '../theme/tokens';
import { cn } from '../utils/cn';
import { Spinner } from './Spinner';
import {
  ButtonIntent,
  ButtonSize,
  ButtonVariant,
  DsPrimitiveProps,
} from './types';

export interface ButtonProps extends DsPrimitiveProps {
  variant?: ButtonVariant;
  intent?: ButtonIntent;
  size?: ButtonSize;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  loading?: boolean;
  startIcon?: React.ReactElement;
  endIcon?: React.ReactElement;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
}

interface StyleProps {
  tokens: DsResolvedTokens;
  bg: string;
  bgHover: string;
  fg: string;
  border: string;
  variant: ButtonVariant;
  height: number;
  minHeight: number;
  paddingX: number;
  isDisabled: boolean;
}

function mapToMuiVariant(variant: ButtonVariant): 'contained' | 'outlined' | 'text' {
  if (variant === 'ghost') return 'text';
  return variant;
}

function resolveColors(
  variant: ButtonVariant,
  intent: ButtonIntent,
  tokens: DsResolvedTokens
) {
  if (intent === 'danger' && variant === 'contained') {
    return {
      bg: tokens.statusError,
      bgHover: tokens.statusError,
      fg: tokens.onPrimary,
      border: 'transparent',
    };
  }
  if (variant === 'contained' && intent === 'secondary') {
    return {
      bg: tokens.surfaceSunken,
      bgHover: tokens.interactionHover,
      fg: tokens.textPrimary,
      border: 'transparent',
    };
  }
  if (variant === 'contained') {
    return {
      bg: tokens.brandPrimary,
      bgHover: tokens.brandPrimaryHover,
      fg: tokens.onPrimary,
      border: 'transparent',
    };
  }
  if (variant === 'outlined') {
    return {
      bg: 'transparent',
      bgHover: tokens.interactionHover,
      fg: tokens.brandPrimary,
      border: tokens.borderDefault,
    };
  }
  return {
    bg: 'transparent',
    bgHover: tokens.interactionHover,
    fg: variant === 'ghost' ? tokens.textPrimary : tokens.brandPrimary,
    border: 'transparent',
  };
}

const useStyles = makeStyles({
  root: (props: StyleProps) => ({
    textTransform: 'none',
    boxShadow: 'none',
    minHeight: props.minHeight,
    height: props.height,
    paddingLeft: props.paddingX,
    paddingRight: props.paddingX,
    borderRadius: props.tokens.radiusMd,
    fontSize: props.tokens.fontSizeBody,
    fontWeight: props.tokens.fontWeightMedium,
    letterSpacing: `${props.tokens.letterSpacingButton}em`,
    transition: `background-color ${props.tokens.motionDurationMicro}ms ${props.tokens.motionEasingStandard}, border-color ${props.tokens.motionDurationMicro}ms ${props.tokens.motionEasingStandard}`,
    backgroundColor: props.variant === 'contained' ? props.bg : props.bg,
    color: props.fg,
    border:
      props.variant === 'outlined'
        ? `${props.tokens.borderWidthDefault}px solid ${props.border}`
        : 'none',
    opacity: props.isDisabled ? props.tokens.opacityDisabled : 1,
    '&:hover': {
      backgroundColor:
        props.variant === 'contained' ? props.bgHover : props.bgHover,
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor:
        props.variant === 'contained' ? props.bgHover : props.tokens.interactionActive,
    },
    '&.Mui-focusVisible': {
      outline: `${props.tokens.borderWidthStrong}px solid ${props.tokens.borderFocus}`,
      outlineOffset: 2,
    },
    '&:focus': {
      outline: 'none',
    },
  }),
  labelLoading: {
    opacity: 0.4,
  },
  spinnerSlot: {
    display: 'inline-flex',
    marginRight: 8,
  },
});

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    id,
    className,
    disabled,
    variant = 'contained',
    intent = 'primary',
    size = 'default',
    type = 'button',
    fullWidth,
    loading = false,
    startIcon,
    endIcon,
    children,
    onClick,
    onFocus,
    onBlur,
    'aria-label': ariaLabel,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();
  const height = size === 'dense' ? tokens.space4 : tokens.space5;
  const paddingX = size === 'dense' ? tokens.space2 : tokens.space3;
  const minHeight = Math.max(height, tokens.touchTargetMin);
  const colors = useMemo(
    () => resolveColors(variant, intent, tokens),
    [variant, intent, tokens]
  );
  const isDisabled = Boolean(disabled || loading);

  const classes = useStyles({
    tokens,
    ...colors,
    variant,
    height,
    minHeight,
    paddingX,
    isDisabled,
  });

  const loadingIcon = (
    <span className={classes.spinnerSlot} aria-hidden="true">
      <Spinner size="sm" inheritColor={variant === 'contained'} decorative />
    </span>
  );

  return (
    <MuiButton
      ref={ref}
      id={id}
      type={type}
      disabled={isDisabled}
      fullWidth={fullWidth}
      variant={mapToMuiVariant(variant)}
      className={cn(classes.root, className)}
      data-testid={testId}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      disableElevation
      disableRipple={false}
      startIcon={loading ? loadingIcon : startIcon}
      endIcon={endIcon}
      onClick={loading ? undefined : onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      classes={{
        label: loading && children ? classes.labelLoading : undefined,
      }}
    >
      {children}
    </MuiButton>
  );
});

Button.displayName = 'Button';
